import { State } from '@meraki-internal/state';
import { ISO8601 } from '../support/ISO8601';
import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { CareMapState } from '../caremap/CareMapState';
import { ILink } from '../caremap/WithLinks';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { AppPrincipal } from '../auth/AppPrincipal';
import { ICareMapSection, ICareMap } from '../caremap/ICareMap';
import { AlertPresenter } from '../AlertPresenter';
import dayjs from 'dayjs';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { IResourceChangedSocketMessage } from '../web-sockets/SocketState';
import { TaskSorter } from './TaskSorter';
import { Logger } from '../support/Logger';

// Task recurrence types
export type TaskRecurrenceType = 'daily' | 'weekly' | 'monthly';
export type TaskRecurrenceOption = 'none' | TaskRecurrenceType;

// Utility functions for handling flexible due date formats
export const isDateOnly = (dueDate: string): boolean => {
    return dueDate.length === 10 && /^\d{4}-[01]\d-[0-3]\d$/.test(dueDate);
};

export const createTaskDueDate = (date: string, time?: string): string => {
    if (!time) {
        // Return date-only format (YYYY-MM-DD)
        return dayjs(date).format('YYYY-MM-DD');
    } else {
        // Combine date and time into ISO datetime
        const baseDate = dayjs(date).startOf('day');
        const timeValue = dayjs(`2024-01-01 ${time}`, 'YYYY-MM-DD h:mm A');
        if (timeValue.isValid()) {
            return baseDate.hour(timeValue.hour()).minute(timeValue.minute()).toISOString();
        }
        // Fallback to date-only if time parsing fails
        return baseDate.format('YYYY-MM-DD');
    }
};

export const extractTimeFromDueDate = (dueDate: string): string | undefined => {
    if (isDateOnly(dueDate)) {
        return undefined;
    }
    // Extract time from datetime and format it as 12-hour
    const dateTime = dayjs(dueDate);
    return dateTime.isValid() ? dateTime.format('h:mm A') : undefined;
};

export interface ITask {
    taskId: string; // Changed from 'id' to match backend
    description: string;
    completed: boolean;
    completedAt?: ISO8601;
    completedBy?: string;
    details?: string;
    dueDate?: string; // Now accepts both date-only (YYYY-MM-DD) and datetime (ISO8601) formats
    displayOrder?: number;
    section?: ICareMapSection;
    supportMemberId?: string;
    tags?: string[];
    source?: {
        type: 'ai-generated' | 'manual' | 'manual-from-review-card';
        noteId?: string;
        recordingId?: string;
        appointmentId?: string;
        migratedAt?: ISO8601;
        originalActionItemId?: string;
    };
    skippedAt?: ISO8601;
    skippedBy?: string;
    version: number;
    createdAt: ISO8601;
    // createdBy / updatedAt / updatedBy are sent only on the singleton (single-task) v2 response,
    // not on list rows (the projection drops them). Mark optional so list-derived tasks type-check.
    createdBy?: string;
    updatedAt?: ISO8601;
    updatedBy?: string;
    links?: {
        self?: ILink;
        delete?: ILink;
        skip?: ILink;
        unskip?: ILink;
        completeForever?: ILink;
        reminder?: ILink;
    };
    // Recurrence fields
    recurrenceRule?: {
        type: TaskRecurrenceType;
        notificationEnabled?: boolean;
        notificationMinutesBefore?: number;
    };
    recurringFromTaskId?: string;
    nextOccurrenceDate?: string;
    lastNotificationScheduledFor?: string;
    recurrenceEnded?: boolean; // For "Complete Forever" functionality
}

interface ITasksState {
    tasks: ITask[];
    loading: boolean;
    loaded: boolean;
    reminderPreferences: Map<string, { enabled: boolean }>;
}


const TASK_MEDIA_TYPE = 'application/vnd.innerhive.task.v2+json';

const defaultState = {
    tasks: [],
    loading: false,
    loaded: false,
    reminderPreferences: new Map()
};

export class TasksState extends State<ITasksState> {
    private careMapId?: string;
    private tasksLink?: ILink;
    private rescheduleTasksLink?: ILink;
    private skipTasksLink?: ILink;
    private pendingToggleRequests: Set<string> = new Set();

    static inject = () => [APIClient, CareMapState, MixPanelEventEmitter, AppPrincipal, AlertPresenter, BufferedEventBus, TaskSorter, Logger];
    constructor(
        private apiClient: APIClient,
        private careMapState: CareMapState,
        private mixpanel: MixPanelEventEmitter,
        private principal: AppPrincipal,
        private alert: AlertPresenter,
        private events: BufferedEventBus,
        private taskSorter: TaskSorter,
        private log: Logger
    ) {
        super(defaultState);

        careMapState.subscribe(() => {
            const careMap = careMapState.getActiveCaremap();

            if (careMap && careMap.careMapId !== this.careMapId) {
                this.careMapId = careMap.careMapId;
                if (careMap.links && careMap.links.tasks) {
                    this.tasksLink = careMap.links.tasks;
                    this.rescheduleTasksLink = careMap.links.rescheduleTasks;
                    this.skipTasksLink = careMap.links.skipTasks;
                    this.load();
                } else {
                    // No tasks link means user doesn't have permission - don't load tasks
                    this.tasksLink = undefined;
                    this.rescheduleTasksLink = undefined;
                    this.skipTasksLink = undefined;
                    this.setState({
                        ...defaultState,
                        loaded: true // Mark as loaded so UI knows there are no tasks available
                    });
                }
            }
        });

        // Listen for websocket events to refresh tasks
        this.events.on('web-sockets.message', (msg?: IResourceChangedSocketMessage) => {
            if (msg) {
                this.onWebsocketMessage(msg);
            }
        });

    }

    state: ITasksState = defaultState;

    getCareMapId = () => {
        return this.careMapId;
    };

    onWebsocketMessage = async (message: IResourceChangedSocketMessage) => {
        if (!this.tasksLink) {
            return;
        }

        // Check if the message is for this caremap's tasks
        if (message.resourceToRefresh.startsWith(this.tasksLink.href)) {
            // Refresh tasks without clearing the list to avoid flicker
            await this.loadWithoutClearing();
        }
    };

    private retryWithBackoff = async <T>(
        operation: () => Promise<T>,
        maxRetries: number = 3,
        baseDelay: number = 1000
    ): Promise<T> => {
        let lastError: any;

        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                return await operation();
            } catch (error: any) {
                lastError = error;

                // Don't retry for certain error types
                if (error.name === 'AbortError' || error.status === 409 || error.errorCode === 'version.conflict') {
                    throw error;
                }

                // Don't retry on final attempt
                if (attempt === maxRetries) {
                    break;
                }

                // Exponential backoff with jitter
                const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }

        throw lastError;
    };

    private normalizeTasks = (tasks: ITask[]): ITask[] => {
        return tasks.map(task => ({
            ...task,
            dueDate: task.dueDate || dayjs().toISOString()
        }));
    };

    private load = async () => {
        if (!this.tasksLink) {
            return;
        }

        this.setState({ tasks: [], loading: true, loaded: false });

        try {
            const { tasks } = await this.apiClient.get(this.tasksLink, TASK_MEDIA_TYPE);
            this.setState({ tasks: this.normalizeTasks(tasks), loading: false, loaded: true });
        } catch (error: any) {
            this.setState({ loading: false, loaded: true });
            error.displayMessage = 'Unable to load tasks. Please try again.';
            this.alert.showAndLogError(error);
        }
    };

    private loadWithoutClearing = async () => {
        if (!this.tasksLink) {
            return;
        }

        // Keep existing tasks visible while loading
        this.setState({ loading: true });

        try {
            const { tasks } = await this.apiClient.get(this.tasksLink, TASK_MEDIA_TYPE);
            this.setState({ tasks: this.normalizeTasks(tasks), loading: false, loaded: true });
        } catch (error: any) {
            this.setState({ loading: false });
            error.displayMessage ='Unable to refresh tasks. Please try again.';
            this.alert.showAndLogError(error);
        }
    };

    getTasks = () => this.state.tasks;

    isLoaded = () => this.state.loaded;

    getTasksByDate = () => {
        const today = dayjs().startOf('day');
        const tomorrow = today.add(1, 'day');
        const dayAfter = today.add(2, 'day');

        const tasksByDate: Record<string, ITask[]> = {
            overdue: [],
            today: [],
            tomorrow: [],
            dayAfter: [],
            future: []
        };

        this.state.tasks.forEach(task => {
            if (!task.dueDate) {
                this.log.error('Task missing dueDate', task);
                return;
            }
            const taskDate = dayjs(task.dueDate).startOf('day');

            if (taskDate.isBefore(today)) {
                tasksByDate.overdue.push(task);
            } else if (taskDate.isSame(today)) {
                tasksByDate.today.push(task);
            } else if (taskDate.isSame(tomorrow)) {
                tasksByDate.tomorrow.push(task);
            } else if (taskDate.isSame(dayAfter)) {
                tasksByDate.dayAfter.push(task);
            } else if (taskDate.isAfter(dayAfter)) {
                tasksByDate.future.push(task);
            }
        });

        // Sort tasks within each date group by time, then by manual ordering
        Object.keys(tasksByDate).forEach(key => {
            tasksByDate[key] = this.taskSorter.sortTasks(tasksByDate[key]);
        });

        return tasksByDate;
    };

    toggleTaskCompletion = async (taskId: string) => {
        const task = this.state.tasks.find(t => t.taskId === taskId);
        if (!task || !this.tasksLink) {
            return;
        }

        // A PUT for this task is already in flight; reflect the click in the UI but don't
        // enqueue a second PUT. Known gap: a follow-up click during the in-flight PUT won't
        // be persisted — the older state wins on the server. Fixing properly would mean
        // (a) plumbing an AbortSignal through HTTPClient/fetch so the in-flight PUT can be
        // cancelled, and (b) firing a fresh PUT with the latest desired state once cancelled.
        if (this.pendingToggleRequests.has(taskId)) {
            const pendingTask = this.state.tasks.find(t => t.taskId === taskId);
            if (!pendingTask) {
                return;
            }

            const willBeCompleted = !pendingTask.completed;
            this.updateTaskInState(taskId, {
                completed: willBeCompleted,
                completedAt: willBeCompleted ? dayjs().toISOString() as ISO8601 : undefined,
                completedBy: willBeCompleted ? this.principal.userId : undefined
            });
            return;
        }

        this.pendingToggleRequests.add(taskId);

        // Get the current task state
        const currentTask = this.state.tasks.find(t => t.taskId === taskId);
        if (!currentTask) {
            this.pendingToggleRequests.delete(taskId);
            return;
        }

        // Optimistic update
        const isCompleted = !currentTask.completed;
        const updates: Partial<ITask> = {
            completed: isCompleted,
            completedAt: isCompleted ? dayjs().toISOString() as ISO8601 : undefined,
            completedBy: isCompleted ? this.principal.userId : undefined
        };

        // If uncompleting a task, clear the recurrenceEnded flag
        if (!isCompleted && currentTask.recurrenceEnded) {
            updates.recurrenceEnded = false;
        }

        this.updateTaskInState(taskId, updates);

        // Debounce to handle rapid clicks
        await new Promise(resolve => setTimeout(resolve, 500));

        try {
            // Get the final state after debounce period
            const finalTask = this.state.tasks.find(t => t.taskId === taskId);
            if (!finalTask) {
                this.pendingToggleRequests.delete(taskId);
                return;
            }

            // Check if task has self link for update permission
            if (!finalTask.links?.self) {
                this.log.error('Task is missing self link, cannot update', finalTask);
                this.pendingToggleRequests.delete(taskId);
                return;
            }

            const updatedTask = this.buildTaskUpdatePayload(finalTask, taskId);

            const response = await this.retryWithBackoff(() =>
                this.apiClient.put(finalTask.links!.self, updatedTask, {
                    accept: TASK_MEDIA_TYPE,
                    'content-type': TASK_MEDIA_TYPE
                })
            );

            // Update task with new version from server response
            if (response) {
                this.updateTaskInState(taskId, {
                    version: response.version,
                    completedAt: response.completedAt,
                    completedBy: response.completedBy
                });

                this.trackTaskCompletion(taskId);

                // For recurring task completion, the v2 server response includes the newly-created
                // next instance; splice it directly so we avoid a full list refetch.
                if (response.nextTask) {
                    this.appendNextTaskFromResponse(response.nextTask);
                }
            }

        } catch (error: unknown) {
            await this.handleToggleError(error);
        } finally {
            // Always clean up the pending request
            this.pendingToggleRequests.delete(taskId);
        }
    };

    skipTask = async (taskId: string) => {
        const task = this.state.tasks.find(t => t.taskId === taskId);
        if (!task?.links?.skip) {
            return;
        }

        // Only allow skipping active tasks (not completed or already skipped)
        if (task.completed || task.skippedAt) {
            // cannot skip task that is already completed or skipped
            return;
        }

        // Optimistic update
        this.updateTaskInState(taskId, {
            skippedAt: dayjs().toISOString() as ISO8601,
            skippedBy: this.principal.userId
        });

        try {
            const response = await this.apiClient.put(task.links.skip, {
                version: task.version
            }, {
                accept: TASK_MEDIA_TYPE,
                'content-type': TASK_MEDIA_TYPE
            });

            // Update task with response from server
            if (response) {
                this.updateTaskInState(taskId, {
                    version: response.version,
                    skippedAt: response.skippedAt,
                    skippedBy: response.skippedBy
                });

                this.mixpanel.track('Task Skipped', () => {
                    return this.getTrackingMeta(task, taskId);
                });

                // For recurring task skip, the v2 server response includes the newly-created
                // next instance; splice it directly so we avoid a full list refetch.
                if (response.nextTask) {
                    this.appendNextTaskFromResponse(response.nextTask);
                }
            }

        } catch (error: any) {
            // Revert optimistic update on error
            this.updateTaskInState(taskId, {
                skippedAt: undefined,
                skippedBy: undefined
            });

            // throw and expect caller to log/show alert
            throw error;
        }
    };

    unskipTask = async (taskId: string) => {
        const task = this.state.tasks.find(t => t.taskId === taskId);
        if (!task?.links?.unskip) {
            return;
        }

        // Only allow unskipping skipped tasks (not completed or already active)
        if (task.completed || !task.skippedAt) {
            // cannot unskip task that is not skipped
            return;
        }

        // Optimistic update
        this.updateTaskInState(taskId, {
            skippedAt: undefined,
            skippedBy: undefined
        });

        try {
            const response = await this.apiClient.put(task.links.unskip, {
                version: task.version
            }, {
                accept: TASK_MEDIA_TYPE,
                'content-type': TASK_MEDIA_TYPE
            });

            // Update task with response from server
            if (response) {
                this.updateTaskInState(taskId, {
                    version: response.version,
                    skippedAt: response.skippedAt,
                    skippedBy: response.skippedBy
                });

                this.mixpanel.track('Task Unskipped', () => {
                    return this.getTrackingMeta(task, taskId);
                });
            }

        } catch (error: unknown) {
            // Revert optimistic update on error
            this.updateTaskInState(taskId, {
                skippedAt: task.skippedAt,
                skippedBy: task.skippedBy
            });

            // throw and expect caller to log/show alert
            throw error;
        }
    };

    completeForever = async (taskId: string) => {
        const task = this.state.tasks.find(t => t.taskId === taskId);
        if (!task?.links?.completeForever) {
            return;
        }

        // Only allow completing forever for recurring tasks that are not already completed/skipped
        if (!task.recurrenceRule || task.completed || task.skippedAt) {
            // cannot complete forever - task must be recurring and active
            return;
        }

        // Optimistic update
        this.updateTaskInState(taskId, {
            completed: true,
            completedAt: dayjs().toISOString() as ISO8601,
            completedBy: this.principal.userId,
            recurrenceEnded: true
        });

        try {
            const response = await this.apiClient.put(task.links.completeForever, {
                version: task.version
            }, {
                accept: TASK_MEDIA_TYPE,
                'content-type': TASK_MEDIA_TYPE
            });

            // Update task with response from server
            if (response) {
                this.updateTaskInState(taskId, {
                    version: response.version,
                    completed: response.completed,
                    completedAt: response.completedAt,
                    completedBy: response.completedBy,
                    recurrenceEnded: response.recurrenceEnded
                });

                this.mixpanel.track('Task Completed Forever', () => {
                    return this.getTrackingMeta(task, taskId);
                });
            }

        } catch (error: unknown) {
            // Revert optimistic update on error
            this.updateTaskInState(taskId, {
                completed: false,
                completedAt: undefined,
                completedBy: undefined,
                recurrenceEnded: false
            });

            // throw and expect caller to log/show alert
            throw error;
        }
    };


    addTask = async (taskData: Partial<ITask>, options?: { reminderEnabled?: boolean }): Promise<ITask | undefined> => {
        if (!this.tasksLink) {
            return undefined;
        }

        const careMap = this.careMapState.getActiveCaremap();
        if (!careMap) {
            return undefined;
        }

        try {
            const displayOrder = this.calculateDisplayOrder(taskData);
            const newTask = this.buildNewTaskPayload(taskData, displayOrder);

            const createdTask = await this.retryWithBackoff(() =>
                this.apiClient.post(this.tasksLink, newTask, {
                    accept: TASK_MEDIA_TYPE,
                    'content-type': TASK_MEDIA_TYPE
                })
            );

            // Optimistically add the created task to the existing list
            const currentTasks = this.state.tasks || [];
            this.setState({
                tasks: [...currentTasks, createdTask]
            });

            this.trackTaskCreation(createdTask, options?.reminderEnabled);

            return createdTask;
        } catch (error: unknown) {
            this.handleAddTaskError(error);
            return undefined;
        }
    };

    updateTask = async (taskId: string, updates: Partial<ITask>, skipTracking = false) => {
        const task = this.state.tasks.find(t => t.taskId === taskId);
        if (!task) {
            return;
        }

        // Check if task has self link for update permission
        if (!task.links?.self) {
            this.log.error('Task is missing self link, cannot update:', task);
            return;
        }

        // Optimistic update - update UI immediately
        this.updateTaskInState(taskId, updates);

        try {
            // Merge updates with existing task, only send editable fields to the backend
            const mergedTask = { ...task, ...updates };
            const updatedTask = this.buildTaskUpdatePayload(mergedTask, taskId);

            const response = await this.retryWithBackoff(() =>
                this.apiClient.put(task.links!.self, updatedTask, {
                    accept: TASK_MEDIA_TYPE,
                    'content-type': TASK_MEDIA_TYPE
                })
            );

            // Track task edit (unless it's a drag operation)
            if (!skipTracking) {
                this.trackTaskEdit(task, updates, taskId);
            }

            // Use the PUT response to sync server-canonical fields rather than refetching the
            // entire list. The optimistic update already reflects the user's intended changes;
            // we just need to pick up server-set fields like version, completedAt, etc.
            if (response) {
                this.updateTaskInState(taskId, {
                    version: response.version,
                    completedAt: response.completedAt,
                    completedBy: response.completedBy,
                    skippedAt: response.skippedAt,
                    skippedBy: response.skippedBy
                });
                if (response.nextTask) {
                    this.appendNextTaskFromResponse(response.nextTask);
                }
            }

        } catch (error: unknown) {
            await this.handleUpdateError(error, taskId, task);
        }
    };

    deleteTask = async (taskId: string) => {
        const task = this.state.tasks.find(t => t.taskId === taskId);
        if (!task) {
            return;
        }

        // Check if task has delete link for delete permission
        if (!task.links?.delete) {
            this.log.error('Task is missing delete link, cannot delete:', task);
            return;
        }

        try {
            await this.apiClient.delete(task.links.delete, { version: task.version });

            this.mixpanel.track('Task Deleted', () => {
                const daysSinceDue = task.dueDate
                    ? dayjs().diff(dayjs(task.dueDate), 'day')
                    : 0;
                return {
                    ...this.getTrackingMeta(task, taskId),
                    'Was Completed': task.completed,
                    'Days Since Due': daysSinceDue,
                    'Tags Count': task.tags?.length || 0
                };
            });

            // Remove from local state immediately
            const tasks = this.state.tasks.filter(t => t.taskId !== taskId);
            this.setState({ tasks });

        } catch (error: any) {
            // Reload on error to get correct state
            await this.load();
            error.displayMessage = 'Unable to delete task. Please try again.';
            this.alert.showAndLogError(error);
        }
    };

    getCompletionStats = () => {
        const total = this.state.tasks.length;
        const completed = this.state.tasks.filter(task => task.completed).length;
        const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

        return { total, completed, percentage };
    };



    // Helper method to build task update payload for API
    private buildTaskUpdatePayload(task: ITask, taskId: string) {
        return {
            userId: (this.careMapState.getActiveCaremap() as ICareMap)?.userId,
            careMapId: this.careMapId,
            taskId: taskId,
            description: task.description,
            completed: task.completed,
            details: task.details,
            dueDate: task.dueDate,
            displayOrder: task.displayOrder,
            section: task.section,
            supportMemberId: task.supportMemberId,
            source: task.source,
            tags: task.tags,
            version: task.version,
            recurrenceRule: task.recurrenceRule,
            recurringFromTaskId: task.recurringFromTaskId,
            nextOccurrenceDate: task.nextOccurrenceDate,
            lastNotificationScheduledFor: task.lastNotificationScheduledFor,
            recurrenceEnded: task.recurrenceEnded
        };
    }

    // Helper method to track task completion/uncompletion
    private trackTaskCompletion(taskId: string) {
        try {
            const task = this.state.tasks.find(t => t.taskId === taskId);
            if (!task) {
                return;
            }

            if (task.completed) {
                this.mixpanel.track('Task Completed', () => {
                    const daysSinceDue = task.dueDate
                        ? dayjs().diff(dayjs(task.dueDate), 'day')
                        : 0;
                    return {
                        ...this.getTrackingMeta(task, taskId),
                        'Days Since Due': daysSinceDue,
                        'Was Overdue': daysSinceDue > 0
                    };
                });
            } else {
                this.mixpanel.track('Task Uncompleted', () => {
                    const daysSinceCompleted = task.completedAt
                        ? dayjs().diff(dayjs(task.completedAt), 'day')
                        : 0;
                    return {
                        ...this.getTrackingMeta(task, taskId),
                        'Days Since Completed': daysSinceCompleted
                    };
                });

            }
        } catch (error: any) {
            error.message = `Failed to track task completion change: ${error.message}`;
            this.log.error(error);
        }
    }

    // Helper method to handle toggle completion errors
    private async handleToggleError(error: any) {
        // Don't revert if the request was cancelled
        if (error.name === 'AbortError') {
            return;
        }

        // For version conflicts, silently reload to sync with server
        if (error.status === 409 || error.errorCode === 'version.conflict') {
            await this.loadWithoutClearing();
            return;
        }

        // For other errors, don't revert UI - keep the optimistic state
    }

    // Helper method to calculate display order for new task
    private calculateDisplayOrder(taskData: Partial<ITask>): number {
        const existingTasks = this.state.tasks.filter(t => {
            const taskDate = dayjs(t.dueDate).startOf('day');
            const newTaskDate = dayjs(taskData.dueDate || dayjs().toISOString()).startOf('day');
            return taskDate.isSame(newTaskDate);
        });

        const hasTime = !isDateOnly(taskData.dueDate || '');
        if (hasTime) {
            // For tasks with time, find tasks at the same time and get max displayOrder
            const taskTime = extractTimeFromDueDate(taskData.dueDate || '');
            const sameTimeTasks = existingTasks.filter(t => {
                const existingTime = extractTimeFromDueDate(t.dueDate || '');
                return existingTime === taskTime;
            });
            if (sameTimeTasks.length > 0) {
                const maxOrder = sameTimeTasks.reduce((max, task) => {
                    const order = task.displayOrder ?? 0;
                    return Math.max(max, order);
                }, 0);
                return maxOrder + 1;
            }
            // No tasks at this time, use 0 (time will handle positioning)
            return 0;
        } else {
            // For untimed tasks, put at end of untimed tasks in this date
            const untimedTasks = existingTasks.filter(t => isDateOnly(t.dueDate || ''));
            const maxOrder = untimedTasks.reduce((max, task) => {
                const order = task.displayOrder ?? dayjs(task.createdAt).valueOf();
                return Math.max(max, order);
            }, 0);
            return maxOrder + 1;
        }
    }

    // Helper method to build new task payload
    private buildNewTaskPayload(taskData: Partial<ITask>, displayOrder: number) {
        const careMap = this.careMapState.getActiveCaremap();
        return {
            userId: (careMap as ICareMap).userId,
            careMapId: careMap!.careMapId,
            description: taskData.description || '',
            completed: taskData.completed || false,
            dueDate: taskData.dueDate || dayjs().startOf('day').format('YYYY-MM-DD'),
            details: taskData.details,
            section: taskData.section || 'patient',
            supportMemberId: taskData.supportMemberId,
            source: taskData.source || { type: 'manual' },
            tags: taskData.tags,
            displayOrder: displayOrder,
            recurrenceRule: taskData.recurrenceRule,
            recurringFromTaskId: taskData.recurringFromTaskId,
            nextOccurrenceDate: taskData.nextOccurrenceDate,
            lastNotificationScheduledFor: taskData.lastNotificationScheduledFor,
            recurrenceEnded: taskData.recurrenceEnded
        };
    }

    // Helper method to track task creation
    private trackTaskCreation(task: ITask, reminderEnabled?: boolean) {
        this.mixpanel.track('Task Created', () => {
            const meta = this.getTrackingMeta(task);
            // Override Notification Reminder if explicitly provided (for new tasks)
            if (reminderEnabled !== undefined) {
                meta['Notification Reminder'] = reminderEnabled ? 'enabled' : 'disabled';
            }
            return {
                ...meta,
                'Tags Count': task.tags?.length || 0
            };
        });
    }

    // Helper method to handle add task errors
    private handleAddTaskError(error: any) {
        error.displayMessage = 'Unable to create task. Please try again.';
        this.alert.showAndLogError(error);
    }

    // Helper method to track task edit
    private trackTaskEdit(task: ITask, updates: Partial<ITask>, taskId: string) {
        try {
            // Only track fields that actually changed
            const fieldsChanged = (Object.keys(updates) as (keyof ITask)[]).filter(key => {
                const oldValue = task[key];
                const newValue = updates[key];
                // Check if the value actually changed
                return oldValue !== newValue &&
                       !(oldValue === undefined && newValue === undefined) &&
                       !(oldValue === null && newValue === null);
            });

            // Only track if there were actual changes
            if (fieldsChanged.length > 0) {
                this.mixpanel.track('Task Edited', () => {
                    const updatedTaskData = { ...task, ...updates };

                    return {
                        ...this.getTrackingMeta(updatedTaskData, taskId),
                        'Fields Changed': fieldsChanged,
                        'Tags Count': updatedTaskData.tags?.length || 0,
                        'Recurrence Changed': fieldsChanged.includes('recurrenceRule')
                    };
                });
            }
        } catch (error: any) {
            error.message = `Failed to track Task Edited: ${error.message}`;
            this.log.error(error);
        }
    }

    // Helper method to handle update task errors
    private async handleUpdateError(error: any, taskId: string, originalTask: ITask) {
        // Revert optimistic update on error
        this.updateTaskInState(taskId, originalTask);
        error.displayMessage = 'Unable to update task. Please try again.';
        this.alert.showAndLogError(error);
    }

    // Helper method to get common tracking metadata for a task
    private getTrackingMeta = (task: ITask, taskId?: string) => {
        const time = extractTimeFromDueDate(task.dueDate || '');
        const wordCount = task.description ? task.description.split(/\s+/).length : 0;
        const daysUntilDue = task.dueDate
            ? dayjs(task.dueDate).diff(dayjs(), 'day')
            : 0;

        // Get reminder preference from cache if available
        const reminderPref = taskId ? this.state.reminderPreferences.get(taskId) : undefined;
        const notificationReminder = reminderPref?.enabled ? 'enabled' : 'disabled';

        return {
            ...(taskId && { 'Task ID': taskId }),
            'Title': task.description || '',
            'Time': time || '',
            'Due Date': task.dueDate || '',
            'Days Until Due': daysUntilDue,
            'Has Additional Details': !!(task.details && task.details.trim()),
            'Title Word Count': wordCount,
            'Has Recurrence': !!task.recurrenceRule,
            'Recurrence Type': task.recurrenceRule?.type || '',
            'Is Recurring Instance': !!task.recurringFromTaskId,
            'Source Type': task.source?.type || 'manual',
            'Source Note ID': task.source?.noteId || '',
            'Source Recording ID': task.source?.recordingId || '',
            'Source Appointment ID': task.source?.appointmentId || '',
            'Notification Reminder': notificationReminder
        };
    };

    // Helper method for optimistic updates
    private updateTaskInState = (taskId: string, updates: Partial<ITask>) => {
        const tasks = this.state.tasks.map(task => {
            if (task.taskId === taskId) {
                return {
                    ...task,
                    ...updates,
                    updatedAt: dayjs().toISOString() as ISO8601
                };
            }
            return task;
        });

        this.setState({ tasks });
    };

    // Splice a server-provided next recurring task into state, ignoring duplicates.
    // Used when a v2 PUT response includes nextTask after completing/skipping a recurring task,
    // so we don't have to refetch the full list.
    private appendNextTaskFromResponse = (nextTask: ITask) => {
        if (this.state.tasks.some(t => t.taskId === nextTask.taskId)) {
            return;
        }
        this.setState({ tasks: [...this.state.tasks, nextTask] });
    };

    getOverdueTasks = () => {
        const today = dayjs().startOf('day');
        return this.state.tasks.filter(task => {
            if (!task.dueDate) {
                return false;
            }
            const taskDate = dayjs(task.dueDate).startOf('day');

            // Task must be originally overdue (due date before today)
            if (!taskDate.isBefore(today)) {
                return false;
            }

            // Include if task is incomplete
            if (!task.completed && !task.skippedAt) {
                return true;
            }

            // Include if task was completed today
            if (task.completed && task.completedAt) {
                const completedDate = dayjs(task.completedAt).startOf('day');
                if (completedDate.isSame(today)) {
                    return true;
                }
            }

            // Include if task was skipped today
            if (task.skippedAt) {
                const skippedDate = dayjs(task.skippedAt).startOf('day');
                if (skippedDate.isSame(today)) {
                    return true;
                }
            }

            return false;
        }).sort((a, b) => {
            // Sort overdue tasks by due date (oldest first), then by time, then by display order
            const aDate = dayjs(a.dueDate).startOf('day');
            const bDate = dayjs(b.dueDate).startOf('day');

            if (!aDate.isSame(bDate)) {
                return aDate.valueOf() - bDate.valueOf(); // Oldest first
            }

            // Same date - use TaskGrouper's comparison logic
            return this.taskSorter.compare(a, b);
        });
    };

    rescheduleOverdueTasks = async (rescheduleOption: 'today' | 'tomorrow' | 'this-weekend' | 'next-week') => {
        if (!this.tasksLink) {
            return;
        }

        const careMap = this.careMapState.getActiveCaremap();
        if (!careMap) {
            return;
        }

        const overdueTasks = this.getOverdueTasks();
        if (overdueTasks.length === 0) {
            return;
        }

        const taskIds = overdueTasks.map(task => task.taskId);

        if (!this.rescheduleTasksLink) {
            return;
        }

        try {
            const requestBody = {
                taskIds: taskIds,
                rescheduleOption: rescheduleOption
            };

            await this.apiClient.put(this.rescheduleTasksLink, requestBody, {
                accept: TASK_MEDIA_TYPE,
                'content-type': TASK_MEDIA_TYPE
            });

            // Reload tasks to get updated data
            await this.loadWithoutClearing();

            this.mixpanel.track('Overdue Tasks Rescheduled', () => ({
                'Reschedule Option': rescheduleOption,
                'Task Count': taskIds.length
            }));

        } catch (error: any) {
            error.displayMessage ='Unable to reschedule tasks. Please try again.';
            this.alert.showAndLogError(error);
        }
    };

    /**
     * Reschedule all overdue and due-today tasks to tomorrow.
     * Single bulk request via the server's /tasks/reschedule endpoint; the response replaces
     * the local task list, so no separate refetch is needed.
     */
    rescheduleAllToTomorrow = async (source?: string) => {
        if (!this.rescheduleTasksLink) {
            return;
        }

        const today = dayjs().startOf('day');
        const tasksToReschedule = this.state.tasks.filter(task => {
            if (task.completed || task.skippedAt || !task.dueDate) {
                return false;
            }

            const taskDate = dayjs(task.dueDate).startOf('day');
            return taskDate.isBefore(today) || taskDate.isSame(today);
        });

        if (tasksToReschedule.length === 0) {
            return;
        }

        const taskIds = tasksToReschedule.map(task => task.taskId);

        try {
            const response = await this.apiClient.put(this.rescheduleTasksLink, {
                taskIds,
                rescheduleOption: 'tomorrow'
            }, {
                accept: TASK_MEDIA_TYPE,
                'content-type': TASK_MEDIA_TYPE
            });

            if (response?.tasks) {
                this.setState({ tasks: this.normalizeTasks(response.tasks) });
            }

            this.mixpanel.track('Rescheduled Remaining Tasks', () => ({
                'Number of Tasks': taskIds.length,
                ...(source && { 'Source': source })
            }));
        } catch (error: any) {
            error.displayMessage ='Unable to reschedule tasks. Please try again.';
            this.alert.showAndLogError(error);
        }
    };

    /**
     * Skip all overdue and due-today tasks
     * Used by tasks status bulk actions
     * Uses bulk skip endpoint for better performance
     */
    skipRemaining = async (source?: string) => {
        if (!this.skipTasksLink) {
            return;
        }

        const today = dayjs().startOf('day');
        const tasksToSkip = this.state.tasks.filter(task => {
            if (task.completed || task.skippedAt || !task.dueDate) {
                return false;
            }

            const taskDate = dayjs(task.dueDate).startOf('day');
            return taskDate.isBefore(today) || taskDate.isSame(today);
        });

        if (tasksToSkip.length === 0) {
            return;
        }

        const taskIds = tasksToSkip.map(task => task.taskId);

        try {
            const requestBody = {
                taskIds: taskIds
            };

            await this.apiClient.put(this.skipTasksLink, requestBody, {
                accept: TASK_MEDIA_TYPE,
                'content-type': TASK_MEDIA_TYPE
            });

            // Reload tasks to get updated data
            await this.loadWithoutClearing();

            this.mixpanel.track('Skipped Remaining Tasks', () => ({
                'Number of Tasks': taskIds.length,
                ...(source && { 'Source': source })
            }));
        } catch (error: any) {
            error.displayMessage ='Unable to skip tasks. Please try again.';
            this.alert.showAndLogError(error);
        }
    };

    getReminderPreference = async (taskId: string): Promise<{ enabled: boolean }> => {
        // Check state first
        if (this.state.reminderPreferences.has(taskId)) {
            return this.state.reminderPreferences.get(taskId)!;
        }

        const task = this.state.tasks.find(t => t.taskId === taskId);
        if (!task?.links?.reminder) {
            return { enabled: false };
        }

        try {
            const response = await this.apiClient.get(task.links.reminder, 'application/json');

            // Store in state (triggers re-render automatically)
            const newPreferences = new Map(this.state.reminderPreferences);
            newPreferences.set(taskId, response);
            this.setState({ reminderPreferences: newPreferences });

            return response;
        } catch (error: any) {
            console.error('Failed to get reminder preference:', error);
            return { enabled: false };
        }
    };

    setReminderPreference = async (taskId: string, enabled: boolean): Promise<void> => {
        const task = this.state.tasks.find(t => t.taskId === taskId);
        if (!task?.links?.reminder) {
            throw new Error('Task not found or missing reminder link');
        }

        // Check if this is actually a change
        const currentPref = this.state.reminderPreferences.get(taskId);
        const wasEnabled = currentPref?.enabled ?? false;
        const isActualChange = wasEnabled !== enabled;

        // Optimistic update in state
        const newPreferences = new Map(this.state.reminderPreferences);
        newPreferences.set(taskId, { enabled });
        this.setState({ reminderPreferences: newPreferences });

        try {
            await this.apiClient.put(task.links.reminder, { enabled }, {
                accept: 'application/json',
                'content-type': 'application/json'
            });

            // Track Task Edited event for reminder preference changes
            if (isActualChange) {
                this.mixpanel.track('Task Edited', () => ({
                    ...this.getTrackingMeta(task, taskId),
                    'Fields Changed': ['reminderEnabled'],
                    'Tags Count': task.tags?.length || 0,
                    'Recurrence Changed': false,
                    'Notification Reminder': enabled ? 'enabled' : 'disabled'
                }));
            }
        } catch (error: any) {
            // Revert on error
            const revertedPreferences = new Map(this.state.reminderPreferences);
            revertedPreferences.delete(taskId);
            this.setState({ reminderPreferences: revertedPreferences });

            error.displayMessage = 'Unable to update reminder preference. Please try again.';
            throw error;
        }
    };
}
